import Table from "@/components/Table/table";
import TitleComplement from "@/features/debtor/claimsTab/components/titleComplement";
import { caseRenderers } from "@/features/debtor/claimsTab/Constant/debtorCustomCellConst";
import { useColumnDefs } from "@/features/debtor/claimsTab/Constant/debtorHeaderConst";
import {
  useGetDebtorClaimListApiDataQuery,
  useLazyGetClaimBasicSearchCreditorTabQuery,
} from "@/services/cases/casesReducer";
import { MotifProgressLoader } from "@ey-xd/motif-react";

import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import CustomPagination from "@/components/Pagination/CustomPagination";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import { useCase } from "@/features/case/use-case";
import { useTranslation } from "react-i18next";
import { cleanPayload } from "@/utility/common";
import BasicSearchDebtorTab from "./BasicSearchDebtorTab";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { useDispatch, useSelector } from "react-redux";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";

export default function ClaimTabContent({ debtorData }: { debtorData?: any }) {
  const { t } = useTranslation();
  const { caseId, debtorId, editId } = useParams();
  const retainState = useSelector(
    (e: any) => e.retainState.debtorClaimsTabState
  );
  const [searchQuery, setSearchQuery] = useState(retainState.SearchText || "");
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  let tableLoadingData = customTableLoadingData;
  const caseData = useCase();
  const abbreviation = caseData?.abbreviation;
  const [isBasicSearchActive, setIsBasicSearchActive] = useState(false);
  const isBasicSearchApplied = retainState.isBasicSearchApplied;
  const [errorMsg, setErrorMsg] = useState("");
  const basicSearchPayload = retainState.basicSearchPayload;
  const [
    triggerBasicSearch,
    {
      data: searchBasicData,
      isFetching: basicSearchFetching,
      error: basicSearchError,
      status: basicSearchStatus,
    },
  ] = useLazyGetClaimBasicSearchCreditorTabQuery();

  const dispatch = useDispatch();
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      CaseID: caseId,
      DebtorId: editId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
    };
    if (retainState?.SortOrder && retainState?.SortColumn) {
      value.SortOrder = retainState?.SortOrder;
      value.SortColumn = retainState?.SortColumn;
    }
    return value;
  }, [editId, caseId, paginationPageSize, retainState]);
  const paginationObj = getPaginationObj;

  let resetSortStatus = useCallback(
    (defaultSort: string) => {
      let json: any = { ...retainState.sortedColumnTypes };
      for (const key in json) {
        json[key] = { sort: null };
      }
      json[defaultSort] = { sort: "desc" };
      return json;
    },
    [retainState.sortedColumnTypes]
  );

  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "debtorClaimsTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }
  const columnDefs = useColumnDefs({ caseId, debtorId, editId, abbreviation });

  const { data, error, isLoading, isFetching, refetch, status } =
    useGetDebtorClaimListApiDataQuery(paginationObj, {
      skip: isBasicSearchApplied,
    });

  useEffect(() => {
    if (isBasicSearchApplied) {
      const cleanedBasicSearchPayload = cleanPayload(basicSearchPayload);
      triggerBasicSearch({ ...cleanedBasicSearchPayload, ...paginationObj });
    } else {
      refetch();
    }
  }, [
    paginationObj,
    basicSearchPayload,
    refetch,
    triggerBasicSearch,
    isBasicSearchApplied,
  ]);

  const handleSearch = useCallback(
    (searchValue: string) => {
      setSearchQuery(searchValue);
      if (searchValue.trim() === "") {
        dispatch(
          setComponentState1({
            entityName: "debtorClaimsTabState",
            entityState: {
              PageNumber: 1,
              PageSize: paginationPageSize,
              SearchText: "",
              SortOrder: "desc",
              SortColumn: "UpdatedOn",
              sortedColumnTypes: {
                ...resetSortStatus("updatedOnType"),
              },
              isBasicSearchApplied: false,
              basicSearchPayload: {},
            },
          })
        );
        setIsBasicSearchActive(false);
      } else {
        dispatch(
          setComponentState1({
            entityName: "debtorClaimsTabState",
            entityState: {
              PageNumber: 1,
              PageSize: paginationPageSize,
              SearchText: searchValue,
              SortOrder: "desc",
              SortColumn: "UpdatedOn",
              sortedColumnTypes: {
                ...resetSortStatus("updatedOnType"),
              },
              isBasicSearchApplied: false,
              basicSearchPayload: {},
            },
          })
        );
        setIsBasicSearchActive(false);
      }
    },
    [dispatch, paginationPageSize, resetSortStatus]
  );

  const handleQuickSearchClear = () => {
    setSearchQuery("");
    dispatch(
      setComponentState1({
        entityName: "debtorClaimsTabState",
        entityState: {
          PageNumber: 1,
          PageSize: paginationPageSize,
          SearchText: "",
          SortOrder: "desc",
          SortColumn: "UpdatedOn",
          sortedColumnTypes: {
            ...resetSortStatus("updatedOnType"),
          },
          isBasicSearchApplied: false,
          basicSearchPayload: {},
        },
      })
    );
    setIsBasicSearchActive(false);
  };

  const headerData = {
    title: "pages.debtors.debtorClaimsSection.tableTitle",
    description: "pages.debtors.debtorClaimsSection.tableDescription",
  };
  const handleBasicSearch = useCallback(
    (searchParams: any) => {
      dispatch(
        setComponentState1({
          entityName: "debtorClaimsTabState",
          entityState: {
            PageNumber: 1,
            PageSize: paginationPageSize,
            SearchText: "",
            SortOrder: "desc",
            SortColumn: "UpdatedOn",
            sortedColumnTypes: {
              ...resetSortStatus("updatedOnType"),
            },
            isBasicSearchApplied: true,
            basicSearchPayload: { ...searchParams },
          },
        })
      );
      setSearchQuery("");
      setIsBasicSearchActive(false);
    },
    [dispatch, paginationPageSize, resetSortStatus]
  );
  if (isLoading) {
    return <MotifProgressLoader show variant="default" />;
  }
  const tableData = isBasicSearchApplied
    ? searchBasicData?.results
    : data?.results
    ? data.results
    : [];
  const datafetching = isFetching || basicSearchFetching;
  const dataError = isBasicSearchApplied ? basicSearchError : error;
  const hasMoreRecords = isBasicSearchApplied
    ? searchBasicData?.hasMoreRecords
    : data?.hasMoreRecords;
  const apiRes = isBasicSearchApplied ? searchBasicData : data;
  const apiStatus = isBasicSearchApplied ? basicSearchStatus : status;
  return (
    <>
      {errorMsg && (
        <NotificationGrid show={true} type="error" message={errorMsg} />
      )}
      <div className={isBasicSearchActive ? "hide_claims_table" : ""}>
        <>
          <Table
            columnDefs={columnDefs}
            columnTypes={{ ...retainState.sortedColumnTypes }}
            handlePaginationData={handlePaginationData}
            paginationObj={paginationObj}
            cellRenderers={datafetching ? [] : tableData}
            TitleComplement={() => (
              <TitleComplement
                onSearch={handleSearch}
                caseId={caseId || ""}
                debtorId={editId || ""}
                handleQuickSearchClear={handleQuickSearchClear}
                searchQuery={searchQuery}
                isBasicSearch={false}
                debtorName={debtorData?.name || ""}
                handleClearBasicSearch={() => {}}
                isBasicSearchApplied={isBasicSearchApplied}
                isBasicSearchActive={isBasicSearchActive}
                setIsBasicSearchActive={setIsBasicSearchActive}
                paginationObj={paginationObj}
                basicSearchPayload={basicSearchPayload}
                setErrorMessage={setErrorMsg}
              />
            )}
            title={headerData.title}
            description={headerData.description}
            caseRenderers={caseRenderers}
            pagination={false}
            overlayNoRowsTemplate={
              datafetching
                ? t(tableLoadingData)
                : dataError
                ? t("global.globalNotifications.wentWrong")
                : t("global.globalNotifications.noRecords")
            }
          />
          {!isBasicSearchActive && (
            <CustomPagination
              apiData={apiRes}
              apiStatus={apiStatus}
              currentpage={paginationObj.PageNumber}
              pageSize={paginationObj.PageSize}
              disableNext={!hasMoreRecords}
              onChangePageSize={(page: number) => {
                dispatch(
                  setComponentState1({
                    entityName: "debtorClaimsTabState",
                    entityState: {
                      PageSize: page,
                      PageNumber: 1,
                    },
                  })
                );
              }}
              onChangeNextPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "debtorClaimsTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber + 1,
                    },
                  })
                );
              }}
              onChangePreviousPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "debtorClaimsTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber - 1,
                    },
                  })
                );
              }}
              pageSizeSelector={paginationPageSizeSelector}
            />
          )}
        </>
      </div>
      {isBasicSearchActive ? (
        <BasicSearchDebtorTab
          name={debtorData?.name || ""}
          onClose={() => setIsBasicSearchActive(false)}
          basicSearchPayload={basicSearchPayload}
          onApply={handleBasicSearch}
        />
      ) : (
        <></>
      )}
    </>
  );
}
